import { useEffect, useRef, type RefObject } from "react";
import { useThree } from "@react-three/fiber";
import * as THREE from "three";

export function usePlacementGestures(gesturesEnabled: RefObject<boolean>, pose: { scale: number }): RefObject<THREE.Group | null> {
  const group = useRef<THREE.Group>(null);
  const { gl } = useThree();
  const gesture = useRef({ pointers: new Map<number, { x: number; y: number }>(), distance: 0, scale: pose.scale });

  useEffect(() => {
    const canvas = gl.domElement;
    const current = gesture.current;
    const onDown = (event: PointerEvent) => {
      if (!gesturesEnabled.current || (event.target as HTMLElement).closest("[data-ui-control]")) return;
      current.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      canvas.setPointerCapture?.(event.pointerId);
      if (current.pointers.size === 2) current.distance = pointerDistance(current.pointers);
    };
    const onMove = (event: PointerEvent) => {
      if (!gesturesEnabled.current) return;
      const previous = current.pointers.get(event.pointerId);
      if (!previous || !group.current) return;
      current.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (current.pointers.size === 1) {
        group.current.rotation.y += (event.clientX - previous.x) * 0.008;
      } else if (current.pointers.size === 2) {
        const nextDistance = pointerDistance(current.pointers);
        if (current.distance > 0) {
          current.scale = THREE.MathUtils.clamp(current.scale * (nextDistance / current.distance), 0.35, 1.15);
          group.current.scale.setScalar(current.scale);
        }
        current.distance = nextDistance;
      }
    };
    const onUp = (event: PointerEvent) => {
      current.pointers.delete(event.pointerId);
      if (current.pointers.size < 2) current.distance = 0;
    };
    canvas.addEventListener("pointerdown", onDown);
    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("pointerup", onUp);
    canvas.addEventListener("pointercancel", onUp);
    return () => {
      for (const pointerId of current.pointers.keys()) {
        if (canvas.hasPointerCapture?.(pointerId)) canvas.releasePointerCapture?.(pointerId);
      }
      current.pointers.clear();
      current.distance = 0;
      canvas.removeEventListener("pointerdown", onDown);
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerup", onUp);
      canvas.removeEventListener("pointercancel", onUp);
    };
  }, [gesturesEnabled, gl]);

  return group;
}

function pointerDistance(pointers: Map<number, { x: number; y: number }>): number {
  const points = [...pointers.values()];
  if (!points[0] || !points[1]) return 0;
  return Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
}
